/* eslint-disable react/no-unescaped-entities */
import { useState } from 'react';
import axios from 'axios';
import { HOME } from '@/constants/testIds';
import { track } from '@/lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR_OPTIONS = [
  'Black', 'White', 'Brown', 'Yellow', 'Pink', 'Red', 'Blue', 'Navy Blue',
  'Green', 'Orange', 'Purple', 'Grey', 'Surprise me',
];
const SIZE_OPTIONS = ['S', 'M', 'L', 'XL', '2XL', '3XL'];
const GARMENT_OPTIONS = ['T-Shirt', 'Hoodie'];
const MAX_REFERENCES = 8;
const OCCASION_OPTIONS = [
  'School / Team',
  'Sporting Event',
  'Bridal Shower / Wedding',
  'Birthday',
  'Family / Reunion',
  'Church / Church Group',
  'Business / Brand',
  'Just for me',
  'Gift',
  'Other',
];
const TIMELINE_OPTIONS = [
  'ASAP — I need it yesterday',
  'Within 2 weeks',
  '3–4 weeks',
  'No rush — quality first',
];

const Field = ({ label, htmlFor, children, error }) => (
  <label htmlFor={htmlFor} className="block">
    <span className="font-body text-xs tracking-[0.3em] uppercase text-black/60 mb-2 block">
      {label}
    </span>
    {children}
    {error && (
      <span className="font-body text-xs text-red-600 mt-1 block">{error}</span>
    )}
  </label>
);

const PillGroup = ({ options, value, onChange, name }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const isDisabled = /coming soon/i.test(opt);
      return (
        <button
          key={opt}
          type="button"
          onClick={() => !isDisabled && onChange(opt)}
          disabled={isDisabled}
          data-testid={`${name}-${opt}`}
          className={[
            'inline-flex items-center font-display font-bold text-xs uppercase tracking-wide',
            'px-4 py-2 rounded-full border-2 transition-colors',
            isDisabled
              ? 'bg-black/5 text-black/40 border-black/15 cursor-not-allowed'
              : value === opt
                ? 'bg-black text-brand-orange border-black'
                : 'bg-white text-black border-black/20 hover:border-black',
          ].join(' ')}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const CustomRequestSection = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    idea: '',
    timeline: 'Within 2 weeks',
    occasion: 'Just for me',
    agreed_to_disclaimer: false,
  });
  const [items, setItems] = useState([
    { garment: 'T-Shirt', color: 'Black', size: 'M', quantity: 1 },
  ]);
  const [refFiles, setRefFiles] = useState([]); // File[]
  const [refPreviews, setRefPreviews] = useState([]); // string[] blob urls (parallel to refFiles)
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [rolling, setRolling] = useState(false);

  const rollIdea = async () => {
    if (rolling) return;
    setRolling(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/api/idea-generator`, {
        occasion: form.occasion,
        garment: items[0]?.garment,
        hint: form.idea?.trim() ? form.idea.trim().slice(0, 280) : undefined,
      });
      if (data?.idea) {
        setForm((f) => ({ ...f, idea: data.idea.slice(0, 2000) }));
        track('idea_rolled', { occasion: form.occasion, garment: items[0]?.garment });
      }
    } catch {
      setError('Teeodore is out to lunch — try rolling again in a sec.');
    } finally {
      setRolling(false);
    }
  };

  const update = (field) => (v) => setForm((f) => ({ ...f, [field]: v }));

  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const addItem = () => {
    setItems((prev) => [...prev, { garment: 'T-Shirt', color: 'Black', size: 'M', quantity: 1 }]);
  };
  const removeItem = (idx) => {
    setItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };
  const totalQty = items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);

  const addFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList);
    setRefFiles((prev) => {
      const next = [...prev];
      const nextPreviews = [...refPreviews];
      for (const file of incoming) {
        if (next.length >= MAX_REFERENCES) break;
        next.push(file);
        nextPreviews.push(
          file.type && file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        );
      }
      setRefPreviews(nextPreviews);
      return next;
    });
  };
  const removeFile = (idx) => {
    setRefFiles((prev) => prev.filter((_, i) => i !== idx));
    setRefPreviews((prev) => {
      const removed = prev[idx];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.agreed_to_disclaimer) {
      setError('Please review and accept the content policy before submitting.');
      return;
    }
    if (form.idea.trim().length < 10) {
      setError('Tell me a bit more about your idea — at least 10 characters.');
      return;
    }
    if (items.length === 0 || items.some((it) => !it.quantity || Number(it.quantity) < 1)) {
      setError('Add at least one item with a quantity of 1 or more.');
      return;
    }
    setSubmitting(true);
    try {
      // Upload every reference file first (sequentially — keeps errors clear).
      const references = [];
      for (const file of refFiles) {
        const fd = new FormData();
        fd.append('file', file);
        // eslint-disable-next-line no-await-in-loop
        const up = await axios.post(`${API}/api/custom-requests/upload`, fd);
        references.push({ url: up.data.url, filename: up.data.filename });
      }
      const payload = {
        ...form,
        items: items.map((it) => ({
          garment: it.garment,
          color: it.color,
          size: it.size,
          quantity: Number(it.quantity) || 1,
        })),
        reference_images: references,
        // Legacy top-level fields, populated from the first item so old
        // downstream code (admin summary, CSV, etc.) still displays something.
        garment: items[0]?.garment,
        shirt_color: items[0]?.color,
        size: items[0]?.size,
        quantity: String(totalQty),
      };
      await axios.post(`${API}/api/custom-requests`, payload);
      track('custom_request_submitted', {
        garment: payload.garment,
        color: payload.shirt_color,
        items_count: items.length,
        total_qty: totalQty,
        has_reference: references.length > 0,
      });
      setSubmitted(true);
    } catch (err) {
      setError(
        err.response?.data?.detail
        || 'Submission failed. Please try again in a moment.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="custom"
      data-testid={HOME.customSection}
      className="relative bg-[#FFF7EE] text-black py-28 px-6 sm:px-12 overflow-hidden"
    >
      {/* Mascot watermark */}
      <img
        src="/brand/toilet-thinker.png"
        alt=""
        aria-hidden="true"
        className="hidden lg:block pointer-events-none select-none absolute -right-16 bottom-0 w-[28rem] opacity-[0.08]"
        draggable="false"
      />

      <div className="max-w-4xl mx-auto relative">
        <div className="mb-12">
          <p className="font-body text-xs sm:text-sm tracking-[0.4em] uppercase text-brand-orange mb-3">
            Got an idea? →
          </p>
          <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-[-0.02em]">
            Tell me what's<br />
            rattling around <span className="text-brand-orange">your brain.</span>
          </h2>
          <p className="font-body text-base text-black/70 mt-6 max-w-xl">
            Submit your idea and I'll come back with a sketch + a quote.
            One-of-one tees or small runs — your call.
          </p>
        </div>

        {submitted ? (
          <div
            data-testid={HOME.customSuccess}
            className="relative border-2 border-black rounded-3xl p-10 bg-white shadow-[10px_10px_0_#FF6600]"
          >
            {/* Teeodore giving the thumbs-up on the submit */}
            <img
              src="/brand/teeodore-sm.png"
              alt="Teeodore gives your idea a thumbs up"
              className="hidden sm:block absolute -top-16 -right-6 md:-right-10 w-32 md:w-40 select-none drop-shadow-[0_10px_25px_rgba(255,102,0,0.35)] pointer-events-none"
              draggable={false}
            />
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-brand-orange/15 border border-brand-orange/40">
              <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" />
              <span className="font-display font-black text-[10px] tracking-[0.32em] uppercase text-brand-orange">
                Teeodore is on it
              </span>
            </div>
            <div className="font-display font-black text-3xl sm:text-4xl text-black mb-3">
              Idea received. <span className="text-brand-orange">Flushed.</span>
            </div>
            <p className="font-body text-base text-black/70 leading-relaxed max-w-lg">
              I&rsquo;ll review your idea and get back to you at <strong>{form.email}</strong> within a few days
              with a rough sketch and pricing. If we&rsquo;re a fit, I&rsquo;ll send a secure checkout link to start
              the build.
            </p>
            <button
              type="button"
              onClick={() => { setSubmitted(false); setForm({ ...form, idea: '', agreed_to_disclaimer: false }); }}
              className="mt-6 inline-flex items-center gap-2 font-display font-bold text-sm uppercase tracking-wide bg-black text-white rounded-full px-6 py-3 hover:bg-brand-orange transition-colors"
            >
              Submit another idea →
            </button>
          </div>
        ) : (
          <form
            data-testid={HOME.customForm}
            onSubmit={onSubmit}
            className="border-2 border-black rounded-3xl p-6 sm:p-10 bg-white shadow-[10px_10px_0_#FF6600] grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Prominent policy + pricing banner — customers see this BEFORE they type a single character */}
            <div className="md:col-span-2 -mt-1 mb-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href="/policies/pricing"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="custom-pricing-link"
                className="group flex items-center gap-3 rounded-2xl border-2 border-black bg-brand-cream px-4 py-3 hover:bg-brand-orange transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-black text-brand-orange flex items-center justify-center font-display font-black text-lg group-hover:bg-white group-hover:text-black transition-colors">$</div>
                <div className="min-w-0">
                  <div className="font-display font-black text-[10px] tracking-[0.24em] uppercase text-black/60 group-hover:text-black">See prices first</div>
                  <div className="font-display font-black text-sm text-black leading-tight">Pricing Guide →</div>
                </div>
              </a>
              <a
                href="/policies/payment"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="custom-policy-link"
                className="group flex items-center gap-3 rounded-2xl border-2 border-black bg-brand-cream px-4 py-3 hover:bg-brand-orange transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-black text-brand-orange flex items-center justify-center font-display font-black text-lg group-hover:bg-white group-hover:text-black transition-colors">§</div>
                <div className="min-w-0">
                  <div className="font-display font-black text-[10px] tracking-[0.24em] uppercase text-black/60 group-hover:text-black">Please read</div>
                  <div className="font-display font-black text-sm text-black leading-tight">Payment Policy →</div>
                </div>
              </a>
            </div>
            <Field label="Your name" htmlFor="cr-name">
              <input
                id="cr-name"
                data-testid={HOME.customName}
                type="text"
                required
                value={form.name}
                onChange={(e) => update('name')(e.target.value)}
                className="w-full bg-transparent border-b-2 border-black/30 focus:border-brand-orange outline-none py-3 font-body text-base text-black placeholder-black/30"
                placeholder="What should I call you?"
              />
            </Field>

            <Field label="Email" htmlFor="cr-email">
              <input
                id="cr-email"
                data-testid={HOME.customEmail}
                type="email"
                required
                value={form.email}
                onChange={(e) => update('email')(e.target.value)}
                className="w-full bg-transparent border-b-2 border-black/30 focus:border-brand-orange outline-none py-3 font-body text-base text-black placeholder-black/30"
                placeholder="you@example.com"
              />
            </Field>

            <div className="md:col-span-2">
              <Field label="Your idea" htmlFor="cr-idea">
                <textarea
                  id="cr-idea"
                  data-testid={HOME.customIdea}
                  required
                  rows={5}
                  minLength={10}
                  maxLength={2000}
                  value={form.idea}
                  onChange={(e) => update('idea')(e.target.value)}
                  className="w-full bg-transparent border-2 border-black/20 focus:border-brand-orange outline-none rounded-2xl px-4 py-3 font-body text-base text-black placeholder-black/30 resize-none"
                  placeholder="Describe the shirt you want me to make. Style, mood, specific colors (heather green, charcoal, etc.), references — go wild. The weirder the better."
                />
                <div className="flex items-center justify-between mt-1">
                  <button
                    type="button"
                    data-testid="custom-roll-idea"
                    onClick={rollIdea}
                    disabled={rolling}
                    className={[
                      'inline-flex items-center gap-2 rounded-full px-4 py-2 font-display font-black text-[11px] uppercase tracking-[0.24em] transition-all',
                      rolling
                        ? 'bg-black/60 text-white/60 cursor-wait'
                        : 'bg-black text-brand-orange hover:bg-brand-orange hover:text-black border-2 border-black',
                    ].join(' ')}
                  >
                    {rolling ? (
                      <>
                        <span className="w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                        Teeodore is thinking…
                      </>
                    ) : (
                      <>🎲 Roll me an idea</>
                    )}
                  </button>
                  <div className="text-xs text-black/40 font-body">
                    {form.idea.length} / 2000
                  </div>
                </div>
              </Field>
            </div>

            {/* Reference designs upload (optional, multiple) */}
            <div className="md:col-span-2">
              <Field label={`Got designs or references? (up to ${MAX_REFERENCES}, optional)`}>
                <label
                  htmlFor="cr-reference"
                  data-testid={HOME.customReferenceDrop}
                  className={[
                    'block rounded-2xl border-2 border-dashed cursor-pointer transition-colors',
                    refFiles.length > 0
                      ? 'border-brand-orange bg-orange-50/40'
                      : 'border-black/20 bg-black/[0.02] hover:border-brand-orange hover:bg-orange-50/30',
                    'px-5 py-6',
                  ].join(' ')}
                >
                  {refFiles.length === 0 ? (
                    <div className="flex items-start gap-4">
                      <div className="font-display font-black text-4xl text-brand-orange leading-none">+</div>
                      <div>
                        <div className="font-display font-black text-base text-black">
                          Drop your designs or sketches here
                        </div>
                        <p className="font-body text-sm text-black/60 mt-1">
                          Got a logo, photos, or rough sketches you want me to work from? Attach up to {MAX_REFERENCES} files.
                          Totally optional — describing the idea is enough.
                        </p>
                        <p className="font-body text-xs text-black/50 mt-3 leading-relaxed">
                          <strong>Best formats:</strong> <span className="text-black/80">PNG</span> (logos with clear background) ·
                          <span className="text-black/80"> JPG / JPEG</span> (photos or color references) ·
                          <span className="text-black/80"> SVG / PDF</span> (vector — sharpest print).
                          WebP, HEIC and Illustrator/Photoshop files (.AI / .PSD) also accepted. Max 25 MB each.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {refFiles.map((file, i) => (
                          <div
                            key={`${file.name}-${i}`}
                            className="relative rounded-xl overflow-hidden bg-black/5 border-2 border-black/10 group"
                          >
                            <div className="aspect-square flex items-center justify-center">
                              {refPreviews[i] ? (
                                <img src={refPreviews[i]} alt="preview" className="w-full h-full object-cover" />
                              ) : (
                                <span className="font-display font-black text-xs uppercase tracking-[0.18em] text-black/60 px-2 text-center">
                                  {(file.name.split('.').pop() || 'file').toUpperCase()}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              data-testid={`${HOME.customReferenceRemove}-${i}`}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeFile(i); }}
                              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/80 text-white text-xs font-black hover:bg-red-600 transition-colors flex items-center justify-center"
                              aria-label="Remove file"
                            >
                              ×
                            </button>
                            <div className="px-2 py-1.5 text-[10px] font-body text-black/70 truncate bg-white">
                              {file.name}
                            </div>
                          </div>
                        ))}
                        {refFiles.length < MAX_REFERENCES && (
                          <div className="aspect-square rounded-xl border-2 border-dashed border-black/25 hover:border-brand-orange flex flex-col items-center justify-center text-black/50 hover:text-brand-orange">
                            <div className="text-3xl font-display font-black leading-none">+</div>
                            <div className="font-body text-[10px] tracking-[0.18em] uppercase mt-1">
                              Add more
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mt-3 text-xs text-black/50 font-body">
                        {refFiles.length} of {MAX_REFERENCES} attached
                      </div>
                    </div>
                  )}
                  <input
                    id="cr-reference"
                    data-testid={HOME.customReferenceFile}
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.bmp,.tiff,.tif,.pdf,.svg,.ai,.psd,image/*"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    className="hidden"
                  />
                </label>
              </Field>
            </div>

            {/* Line-item picker — multiple garment/color/size/quantity rows */}
            <div className="md:col-span-2">
              <Field
                label={`Items in your order (${totalQty} piece${totalQty === 1 ? '' : 's'})`}
              >
                <p className="font-body text-xs text-black/50 mb-3">
                  Add one row per style. Doing a team order? Mix and match garments, colors and sizes freely.
                  Tees are <strong>Gildan Softstyle</strong> · Hoodies are heavyweight fleece · Sizes <strong>2XL / 3XL +$5</strong>.
                </p>
                <div className="space-y-3">
                  {items.map((it, idx) => (
                    <div
                      key={idx}
                      data-testid={`custom-item-row-${idx}`}
                      className="rounded-2xl border-2 border-black/15 bg-white p-4 sm:p-5"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-display font-black text-xs tracking-[0.24em] uppercase text-black/60">
                          Item {idx + 1}
                        </div>
                        {items.length > 1 && (
                          <button
                            type="button"
                            data-testid={`custom-item-remove-${idx}`}
                            onClick={() => removeItem(idx)}
                            className="font-display font-bold text-[11px] uppercase tracking-[0.18em] text-black/50 hover:text-red-600 transition-colors"
                          >
                            🗑 Remove
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <div className="font-body text-[10px] tracking-[0.24em] uppercase text-black/50 mb-1.5">Garment</div>
                          <select
                            data-testid={`custom-item-garment-${idx}`}
                            value={it.garment}
                            onChange={(e) => updateItem(idx, 'garment', e.target.value)}
                            className="w-full bg-white border-2 border-black/20 focus:border-brand-orange outline-none rounded-xl px-3 py-2.5 font-body text-sm text-black"
                          >
                            {GARMENT_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
                          </select>
                        </div>
                        <div>
                          <div className="font-body text-[10px] tracking-[0.24em] uppercase text-black/50 mb-1.5">Color</div>
                          <select
                            data-testid={`custom-item-color-${idx}`}
                            value={it.color}
                            onChange={(e) => updateItem(idx, 'color', e.target.value)}
                            className="w-full bg-white border-2 border-black/20 focus:border-brand-orange outline-none rounded-xl px-3 py-2.5 font-body text-sm text-black"
                          >
                            {COLOR_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <div className="font-body text-[10px] tracking-[0.24em] uppercase text-black/50 mb-1.5">Size</div>
                          <select
                            data-testid={`custom-item-size-${idx}`}
                            value={it.size}
                            onChange={(e) => updateItem(idx, 'size', e.target.value)}
                            className="w-full bg-white border-2 border-black/20 focus:border-brand-orange outline-none rounded-xl px-3 py-2.5 font-body text-sm text-black"
                          >
                            {SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div>
                          <div className="font-body text-[10px] tracking-[0.24em] uppercase text-black/50 mb-1.5">Qty</div>
                          <input
                            data-testid={`custom-item-qty-${idx}`}
                            type="number"
                            min={1}
                            max={10000}
                            value={it.quantity}
                            onChange={(e) => updateItem(idx, 'quantity', e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1))}
                            className="w-full bg-white border-2 border-black/20 focus:border-brand-orange outline-none rounded-xl px-3 py-2.5 font-body text-sm text-black"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid="custom-item-add"
                  onClick={addItem}
                  className="mt-3 inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-[0.2em] px-4 py-2.5 rounded-full border-2 border-black bg-white text-black hover:bg-black hover:text-brand-orange transition-colors"
                >
                  + Add another item
                </button>
              </Field>
            </div>

            <Field label="How soon do you need it?">
              <PillGroup
                options={TIMELINE_OPTIONS}
                value={form.timeline}
                onChange={update('timeline')}
                name={HOME.customTimeline}
              />
            </Field>

            <div className="md:col-span-2">
              <Field label="Is your custom shirt for…">
                <PillGroup
                  options={OCCASION_OPTIONS}
                  value={form.occasion}
                  onChange={update('occasion')}
                  name={HOME.customOccasion}
                />
                <p className="font-body text-xs text-black/50 mt-2">
                  Helps me match the vibe, turnaround, and pricing to what you're building.
                </p>
              </Field>
            </div>

            {/* Disclaimer */}
            <div className="md:col-span-2 bg-[#FFF7EE] border-2 border-black rounded-2xl p-5">
              <div className="font-display font-black text-sm uppercase tracking-[0.2em] text-brand-orange mb-2">
                Content Policy
              </div>
              <p className="font-body text-sm text-black/80 leading-relaxed mb-4">
                Shirt Happens Studio is happy to bring just about any idea to life. To keep this studio
                a creative space I'm proud of, I won't take on designs that are vulgar, hateful, racist,
                or that conflict with my personal beliefs. I reserve the right to decline any request that
                falls outside those values, and a full refund will be issued if a paid order is rejected
                on these grounds.
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={HOME.customAgree}
                  checked={form.agreed_to_disclaimer}
                  onChange={(e) => update('agreed_to_disclaimer')(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-brand-orange"
                />
                <span className="font-body text-sm text-black/80">
                  I've read the content policy above and I'm submitting an idea in good faith. Custom orders follow the <a href="/policies/payment" target="_blank" rel="noopener noreferrer" className="text-brand-orange underline hover:text-black transition-colors">payment policy</a>.
                </span>
              </label>
            </div>

            {error && (
              <div
                data-testid={HOME.customError}
                className="md:col-span-2 px-5 py-3 rounded-2xl bg-red-50 border-2 border-red-300 text-red-700 font-body text-sm"
              >
                {error}
              </div>
            )}

            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <p className="font-body text-xs text-black/50">
                I'll only use your email to reply about this idea.
              </p>
              <button
                type="submit"
                data-testid={HOME.customSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-3 font-display font-bold text-sm sm:text-base uppercase tracking-wide bg-black text-white rounded-full px-8 py-4 hover:bg-brand-orange transition-colors shadow-[6px_6px_0_#FF6600] hover:shadow-[6px_6px_0_#000] disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Send my idea'} <span aria-hidden="true">→</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default CustomRequestSection;
